/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import React, { createContext, useContext, forwardRef, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import mergeRefs from 'react-merge-refs';
import styled, { css, ThemeContext } from 'styled-components';
import { retrieveComponentStyles, DEFAULT_THEME, getColor, focusStyles } from '@zendeskgarden/react-theming';
import { stripUnit } from 'polished';
import { useTabs } from '@zendeskgarden/container-tabs';
import { getControlledValue } from '@zendeskgarden/container-utilities';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

const COMPONENT_ID$3 = 'tabs.tab';
const colorStyles = _ref => {
  let {
    theme,
    isSelected
  } = _ref;
  const selectedColor = getColor('primaryHue', 600, theme);
  return css(["border-color:", ";color:", ";&:hover{color:", ";}", " &:active{border-color:currentcolor;color:", ";}&[aria-disabled='true']{border-color:transparent;color:", ";}"], isSelected && 'currentcolor !important', isSelected ? selectedColor : 'inherit', selectedColor, focusStyles({
    theme,
    inset: true,
    spacerWidth: null,
    shadowWidth: 'sm',
    selector: '&:focus-visible::before, &[data-garden-focus-visible="true"]::before',
    styles: {
      color: selectedColor
    }
  }), selectedColor, props => getColor('neutralHue', 400, props.theme));
};
const sizeStyles = _ref2 => {
  let {
    theme
  } = _ref2;
  const paddingTop = theme.space.base * 2.5;
  const paddingHorizontal = theme.space.base * 7;
  const paddingBottom = paddingTop - stripUnit(theme.borderWidths.md) - stripUnit(theme.borderWidths.sm);
  return css(["padding:", "px ", "px ", "px;"], paddingTop, paddingHorizontal, paddingBottom);
};
const StyledTab = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$3,
  'data-garden-version': '8.69.4'
}).withConfig({
  displayName: "StyledTab",
  componentId: "sc-x2pbow-0"
})(["display:inline-block;position:relative;transition:color 0.25s ease-in-out;border-bottom:", " transparent;border-width:", ";cursor:pointer;overflow:hidden;vertical-align:top;user-select:none;text-align:center;text-decoration:none;text-overflow:ellipsis;", " ", " &:focus{text-decoration:none;}&::before{transition:box-shadow 0.1s ease-in-out;content:'';}&:focus-visible::before,&[data-garden-focus-visible]::before{position:absolute;top:", "px;right:", "px;left:", "px;border-radius:", ";height:", "px;pointer-events:none;}&:active::before{box-shadow:none;}&[aria-disabled='true']{cursor:default;}", ";"], props => props.theme.borderStyles.solid, props => props.theme.borderWidths.md, sizeStyles, colorStyles, props => props.theme.space.base * 2.5, props => props.theme.space.base * 6, props => props.theme.space.base * 6, props => props.theme.borderRadii.md, props => props.theme.space.base * 5, props => retrieveComponentStyles(COMPONENT_ID$3, props));
StyledTab.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$2 = 'tabs.tablist';
const StyledTabList = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$2,
  'data-garden-version': '8.69.4'
}).withConfig({
  displayName: "StyledTabList",
  componentId: "sc-wa5aaj-0"
})(["display:block;margin-top:0;margin-bottom:", "px;border-bottom:", " ", " ", ";padding:0;line-height:", "px;white-space:nowrap;color:", ";font-size:", ";", ";"], props => props.theme.space.base * 5, props => props.theme.borderWidths.sm, props => props.theme.borderStyles.solid, props => getColor('neutralHue', 300, props.theme), props => props.theme.space.base * 5, props => getColor('neutralHue', 600, props.theme), props => props.theme.fontSizes.md, props => retrieveComponentStyles(COMPONENT_ID$2, props));
StyledTabList.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$1 = 'tabs.tabpanel';
const StyledTabPanel = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.69.4'
}).withConfig({
  displayName: "StyledTabPanel",
  componentId: "sc-7lhrmp-0"
})(["display:block;&[aria-hidden='true']{display:none;}", ";"], props => retrieveComponentStyles(COMPONENT_ID$1, props));
StyledTabPanel.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID = 'tabs.tabs';
const verticalStyling = _ref => {
  let {
    theme
  } = _ref;
  return css(["display:table;", "{display:table-cell;margin-bottom:0;border-bottom:none;vertical-align:top;}", "{display:block;margin-bottom:", "px;margin-left:", ";border-left:", ";border-bottom-style:none;border-", "-style:", ";border-", "-color:transparent;padding:", "px ", "px;text-align:", ";&:last-of-type{margin-bottom:0;}&:focus-visible::before,&[data-garden-focus-visible]::before{top:", "px;right:", "px;left:", "px;}}", "{margin-", ":", "px;vertical-align:top;}"], StyledTabList, StyledTab, theme.space.base * 5, theme.rtl && '0', theme.rtl && '0', theme.rtl ? 'right' : 'left', theme.borderStyles.solid, theme.rtl ? 'right' : 'left', theme.space.base, theme.space.base * 2, theme.rtl ? 'right' : 'left', theme.space.base, theme.space.base, theme.space.base, StyledTabPanel, theme.rtl ? 'right' : 'left', theme.space.base * 8);
};
const StyledTabs = styled.div.attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.69.4'
}).withConfig({
  displayName: "StyledTabs",
  componentId: "sc-1qaor65-0"
})(["display:block;overflow:hidden;direction:", ";", ";", ";"], props => props.theme.rtl && 'rtl', props => props.isVertical && verticalStyling(props), props => retrieveComponentStyles(COMPONENT_ID, props));
StyledTabs.defaultProps = {
  theme: DEFAULT_THEME
};

const TabsContext = createContext(undefined);
const useTabsContext = () => {
  return useContext(TabsContext);
};

const Tab = React.forwardRef((_ref, ref) => {
  let {
    disabled,
    item,
    ...otherProps
  } = _ref;
  const tabsPropGetters = useTabsContext();
  const tabRef = React.createRef();
  if (disabled || !tabsPropGetters) {
    return React.createElement(StyledTab, _extends({
      role: "tab",
      "aria-disabled": disabled,
      ref: mergeRefs([tabRef, ref])
    }, otherProps));
  }
  const tabProps = tabsPropGetters.getTabProps({
    item,
    focusRef: tabRef,
    index: tabsPropGetters.tabIndexRef.current++
  });
  return React.createElement(StyledTab, _extends({
    isSelected: item === tabsPropGetters.selectedItem
  }, tabProps, otherProps, {
    ref: mergeRefs([tabRef, ref])
  }));
});
Tab.displayName = 'Tab';
Tab.propTypes = {
  disabled: PropTypes.bool,
  item: PropTypes.any
};

const TabList = React.forwardRef((props, ref) => {
  const tabsPropGetters = useTabsContext();
  if (!tabsPropGetters) {
    return React.createElement(StyledTabList, _extends({
      ref: ref
    }, props));
  }
  const tabListProps = tabsPropGetters.getTabListProps();
  return React.createElement(StyledTabList, _extends({}, tabListProps, props, {
    ref: ref
  }));
});
TabList.displayName = 'TabList';

const TabPanel = React.forwardRef((_ref, ref) => {
  let {
    item,
    ...otherProps
  } = _ref;
  const tabsPropGetters = useTabsContext();
  if (!tabsPropGetters) {
    return React.createElement(StyledTabPanel, _extends({
      ref: ref
    }, otherProps));
  }
  const tabPanelProps = tabsPropGetters.getTabPanelProps({
    item,
    index: tabsPropGetters.tabPanelIndexRef.current++
  });
  return React.createElement(StyledTabPanel, _extends({
    "aria-hidden": tabsPropGetters.selectedItem !== item
  }, tabPanelProps, otherProps, {
    ref: ref
  }));
});
TabPanel.displayName = 'TabPanel';
TabPanel.propTypes = {
  item: PropTypes.any
};

const Tabs = forwardRef((_ref, ref) => {
  let {
    isVertical,
    children,
    onChange,
    selectedItem: controlledSelectedItem,
    ...otherProps
  } = _ref;
  const theme = useContext(ThemeContext) || DEFAULT_THEME;
  const [internalSelectedItem, setInternalSelectedItem] = useState();
  const tabIndexRef = useRef(0);
  const tabPanelIndexRef = useRef(0);
  const selectedItem = getControlledValue(controlledSelectedItem, internalSelectedItem);
  const tabPropGetters = useTabs({
    rtl: theme.rtl,
    orientation: isVertical ? 'vertical' : 'horizontal',
    selectedItem,
    defaultSelectedIndex: 0,
    onSelect: item => {
      if (onChange) {
        onChange(item);
      } else {
        setInternalSelectedItem(item);
      }
    }
  });
  const tabsContextValue = useMemo(() => ({
    ...tabPropGetters,
    tabIndexRef,
    tabPanelIndexRef
  }), [tabPropGetters]);
  return React.createElement(TabsContext.Provider, {
    value: tabsContextValue
  }, React.createElement(StyledTabs, _extends({
    isVertical: isVertical
  }, otherProps, {
    ref: ref
  }), children));
});
Tabs.propTypes = {
  isVertical: PropTypes.bool,
  selectedItem: PropTypes.any,
  onChange: PropTypes.func
};
Tabs.defaultProps = {
  isVertical: false
};
Tabs.displayName = 'Tabs';

export { Tab, TabList, TabPanel, Tabs };
